import React, { useEffect, useState } from "react";
import { ScrollText, X, Check, SkipForward, Star, Swords, Trophy, Map, Compass, Gift, Landmark } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CAT_ICON = {
  skilling: Star, quests: ScrollText, diaries: Map,
  combat: Swords, raids: Landmark, collection: Trophy, clues: Compass, login: Gift,
};
const CAT_LABEL = {
  skilling: "Skilling", quests: "Quest", diaries: "Diary",
  combat: "Combat", raids: "Raid", collection: "Collection", clues: "Clue", login: "Login bonus",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function TaskHistory({ profileId, reloadKey }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get(`/profiles/${profileId}/task-history`)
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profileId, reloadKey]);

  const completed = rows.filter((r) => r.status !== "skipped");
  const totalPts = rows.reduce((s, r) => s + (r.points_awarded || 0), 0);

  return (
    <>
      <button data-testid="open-task-history" onClick={() => setOpen(true)} className="osrs-btn px-3 py-1.5 text-xs flex items-center gap-1.5" title="Your assignment ledger">
        <ScrollText size={14} /> History
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 px-4 py-8 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-5 w-full max-w-lg my-auto" onClick={(e) => e.stopPropagation()} data-testid="task-history-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="osrs-title text-xl flex items-center gap-2"><ScrollText size={20} /> Assignment history</h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-task-history"><X size={16} /></button>
            </div>

            {loading ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-10" data-testid="history-loading">Opening the ledger...</p>
            ) : rows.length === 0 ? (
              <p className="osrs-shadow text-[#b9a06a] text-center py-10" data-testid="history-empty">
                No completed assignments yet. Finish a task and it'll be recorded here.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-3 osrs-shadow text-xs text-[#b9a06a]" data-testid="history-summary">
                  <span><span className="osrs-num text-[#7CFC7C]">{completed.length}</span> completed</span>
                  <span><span className="osrs-num text-[#FFCF3F]">{totalPts.toLocaleString()}</span> total pts</span>
                  <span><span className="osrs-num text-[#FF9966]">{rows.length - completed.length}</span> skipped</span>
                </div>
                <ul className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="history-list">
                  {rows.map((r, i) => {
                    const Icon = CAT_ICON[r.category] || Star;
                    const skipped = r.status === "skipped";
                    return (
                      <li key={i} className="flex items-center gap-3 bg-black/30 border border-[#0f0d09] px-3 py-2" data-testid={`history-row-${i}`}>
                        <Icon size={15} className={skipped ? "text-[#8a7a54] shrink-0" : "text-[#7CFC7C] shrink-0"} />
                        <div className="min-w-0 flex-1">
                          <p className={`osrs-shadow text-sm truncate ${skipped ? "text-[#8a7a54] line-through" : "text-[#E6A519]"}`}>{r.title}</p>
                          <p className="osrs-shadow text-[10px] text-[#8a7a54]">
                            {CAT_LABEL[r.category] || r.category}{r.meta?.tier ? ` · ${r.meta.tier}` : ""} · {fmtDate(r.completed_at)}
                            {r.auto_completed ? " · auto" : ""}
                          </p>
                        </div>
                        {skipped ? (
                          <span className="osrs-num text-[11px] text-[#FF9966] flex items-center gap-1 shrink-0"><SkipForward size={12} /> skip</span>
                        ) : (
                          <span className="osrs-num text-sm text-[#7CFC7C] flex items-center gap-1 shrink-0"><Check size={13} /> +{r.points_awarded}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
